import React, { useMemo } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useTranslation } from 'react-i18next';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import 'dayjs/locale/de';
import 'dayjs/locale/en';
import 'dayjs/locale/es';
import 'dayjs/locale/it';
import axios from 'axios';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  useParams,
  Navigate,
  useSearchParams,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BILLING_TOAST_Z } from './utils/billingToast';
import { isRintintinJwtExpired401, urlLooksLikeNonSession401 } from './utils/session401Guard';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import CookieConsentBanner from './components/CookieConsentBanner';
import MetaPixelPageViews from './components/MetaPixelPageViews';
import PostHogPageViews from './components/PostHogPageViews';
import PostHogIdentitySync from './components/PostHogIdentitySync';
import './styles/native-inputs.css';

const isProduction = process.env.NODE_ENV === 'production';

// Composants (chargés en eager : utilisés sur quasi toutes les pages)
import ProtectedRoute from './components/ProtectedRoute';
import MaintenanceGate from './components/MaintenanceGate';
import Footer from './components/Footer';
import ChatWidget from './components/ChatWidget';
import LaikaChatFlagSync from './components/LaikaChatFlagSync';
import { shouldMountLaikaChat } from './components/LaikaChat/laikaFabVisibility';
import { resolveAssistantUiEnabled } from './components/LaikaChat/resolveAssistantUiEnabled';
import MobileAuthHandler from './components/MobileAuthHandler';
import RouteSuspense from './components/RouteSuspense/RouteSuspense';
import { useAuth } from './context/AuthContext';
import LandingPageV2 from './pages/LandingPageV2';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import { isLocaleRoutesEsEnabled, isLocaleRoutesEnEnabled, isLocaleRoutesDeEnabled, isLocaleRoutesItEnabled, isBlogPath, normalizeMarketingPathname } from './config/localeRoutes';
import { isMarketPath } from './config/marketRoutes';
import { localizedBlogRoutes } from './routes/LocalizedBlogRoutes';
import PrivateLayout from './layouts/PrivateLayout';

// Contexts
import {
  FeatureFlagsProvider,
  useFeatureFlags,
  FeatureFlag,
} from './contexts/FeatureFlagsContext';
import { useBoardingModuleGate } from './hooks/useBoardingModuleVisible';
import RequireCountryFeature from './components/RequireCountryFeature';
import RequireBillingModule from './components/RequireBillingModule';
import { GoogleMapsProvider } from './contexts/GoogleMapsLoaderContext';
import { ChatProvider } from './contexts/ChatContext';
import { useDevNightThemeSnapshot } from './hooks/useDevNightThemeSnapshot';
import { createAppTheme } from './theme/createAppTheme';

// Pages, toutes les routes sont code-splittées via React.lazy().
// Chaque alias correspond au composant lazy ; un préchargement est possible
// via `preloadRoute(path)` (voir Sidebar pour MT-5).
import {
  // Publiques
  LoginPage,
  MaintenancePage,
  AdminHandoffPage,
  SignupUserPage,
  SignUpProPage,
  NoProProfile,
  NoPremiumMessaging,
  TermsOfUse,
  PrivacyPolicy,
  DataDeletion,
  LegalPage,
  GoogleAuthCallback,
  GoogleAccountLinkingCallbackPage,
  PasswordForgottenPage,
  ResetPasswordPage,
  NotFoundPage,
  CGV,
  StripeSetupComplete,
  // SEO marchés + verticales
  BelgiumHubPage,
  BelgiumEducateurCaninPage,
  BelgiumPetSitterPage,
  NetherlandsHubPage,
  NetherlandsEducateurCaninPage,
  NetherlandsPetSitterPage,
  EducateurCaninPage,
  PromeneurCaninPage,
  PetSitterPage,
  LogicielDogSitterPage,
  EcoleClubCaninPage,
  PensionPage,
  ComportementalistePage,
  TailleursPage,
  LoisirCaninPage,
  // Partenaires
  PartenairesPublicPage,
  PartenairesPage,
  PartenaireDetailPage,
  LupiPage,
  OsmozPage,
  OsmozInAppPage,
  MediavetPage,
  TresorsDUlyssePage,
  MfecPage,
  AmbassadeurPage,
  // Subscription
  SubscriptionPage,
  BoardingModuleOfferPage,
  SubSuccessPage,
  BoardingSubSuccessPage,
  SubCancelPage,
  WelcomeTrialOfferPage,
  WelcomePricingTablePage,
  OffreConfigurationPage,
  EFacturationOfferPage,
  AcacedOfferPage,
  AcacedPage,
  SoftwareComparisonPage,
  SoftwareComparisonPetSitterPage,
  SoftwareComparisonPensionPage,
  MigrationPlaybookPage,
  ToolResourcePage,
  AlternativesHubPage,
  VsCanisuiviPage,
  VsHunimalisPage,
  VsKookiePage,
  FaqHubPage,
  AvisRintintinProPage,
  // Blog
  BlogPage,
  BlogArticlePage,
  // Fonctionnalités
  FeaturesPage,
  PlanningReservationsPage,
  MessagingFeaturePage,
  PaiementFacturationPage,
  ForfaitsCreditsPage,
  CartesCadeauxPage,
  EmailsProPage,
  GroupesClientsPage,
  GestionClientsAnimauxPage,
  StatistiquesPage,
  ProfilProfessionnelPage,
  PensionHebergementPage,
  ApplicationMobilePage,
  DoubleSyncGooglePage,
  FraisKilometriquesPage,
  RappelsSmsPage,
  LanguageTestPage,
  WidgetLabPage,
  // Privées (/app)
  AccueilPage,
  StartupPage,
  PlanningPage,
  ReservationsPage,
  ProfilePageV2,
  ClientsChiensPage,
  ClientSegmentsPage,
  AnimalDetailPage,
  AnimalFollowUpPage,
  FollowHubPage,
  ProprioDetailPage,
  AvailabilityPage,
  BookingDetailPage,
  SessionDetailPage,
  AllNotesPage,
  ServicesPage,
  ForfaitsPage,
  SettingsPage,
  BoardingPage,
  StayDetailPage,
  UnavailabilitiesPage,
  BillingPage,
  InvoiceDetailPage,
  QuotesPage,
  AccountingPage,
  FinancePage,
  MarketingToolsPage,
  ProStatsPage,
  ProStatsPageV2,
  TeamPage,
  DesignSystemPage,
  LaikaPage,
  BackOfficeMessagingPage,
  NotificationHistoryPage,
  // Association
  AssociationDashboard,
  AssociationPlanningPage,
  AssociationMembersPage,
  AssociationMemberProfilePage,
  AssociationClientsPage,
  AssociationEventsPage,
  AssociationMessagingPage,
  AssociationCGVPage,
  AssociationSettingsPage,
} from './routes/lazyRoutes';

import PrivateNotFoundPage from './pages/PrivateNotFoundPage';

import './App.css';
import { getApiUrl, rewriteNativeRequestUrl } from './config/apiConfig';
import { configService } from './services/configService';
import {
  emitSessionInvalid,
  registerSessionNavigator,
  unregisterSessionNavigator,
  redirectToLogin,
} from './services/sessionNavigation';
import { AFFILIATE_VIA_STORAGE_KEY } from './constants/affiliate';
import { resolveAmbassadorPromoFromLocation } from './constants/promo';

/** Anciens deep links /app/client/:id (notifications) → route réelle. */
const RedirectLegacyAppClientPath: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  if (!clientId) {
    return <Navigate to="/app/clients" replace />;
  }
  return <Navigate to={`/app/clients/${clientId}`} replace />;
};

/** Ancienne page /app/sms → Communication → onglet SMS (conserve query Stripe). */
const RedirectSmsToCommunication: React.FC = () => {
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  next.set('tab', 'sms');
  return <Navigate to={`/app/marketing?${next.toString()}`} replace />;
};

// Interceptor pour l'authentification ET la correction des URLs
axios.interceptors.request.use(
  config => {
    let url = config.url || '';
    const isNative = Capacitor.isNativePlatform();

    if (isNative) {
      url = rewriteNativeRequestUrl(url);
    } else if (!url.startsWith('http')) {
      // Comportement standard sur navigateur Mac
      const baseUrl = getApiUrl(url);
      url = `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
    }

    config.url = url;

    const token = localStorage.getItem('token');
    // N'ajouter le token que si la requête n'a pas déjà son propre header Authorization.
    // Certaines pages (ex. AdminHandoffPage) passent un JWT différent de celui en localStorage
    // (handoff JWT signé pour un autre utilisateur), l'écraser causerait des 401 ou une
    // connexion au mauvais compte.
    const existingAuth = config.headers['Authorization'] || config.headers['authorization'];
    if (token && !existingAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const nativeH = configService.getNativeClientHeaders();
    if (nativeH) {
      Object.assign(config.headers, nativeH);
    }
    return config;
  },
  error => Promise.reject(error)
);

// Compteur d'erreurs 401 pour éviter les déconnexions sur des erreurs ponctuelles
let authErrorCount = 0;
let lastAuthErrorTime = 0;

axios.interceptors.response.use(
  response => {
    // Réinitialiser le compteur d'erreurs sur une réponse réussie
    authErrorCount = 0;
    return response;
  },
  error => {
    // IMPORTANT: Exclure complètement les endpoints OAuth Google de l'intercepteur
    // pour éviter les interférences pendant le processus d'authentification
    const isOAuthEndpoint = error.config?.url?.includes('/auth/google/callback') ||
      error.config?.url?.includes('/auth/google/authorize') ||
      error.config?.url?.includes('/auth/google/link-account');

    if (isOAuthEndpoint) {
      // Laisser le composant GoogleAuthCallback gérer ses propres erreurs
      return Promise.reject(error);
    }

    // 403 = droits insuffisants sur une ressource : ne jamais enchaîner sur une déconnexion
    if (error.response?.status === 403) {
      return Promise.reject(error);
    }

    // Ne gérer les déconnexions automatiques que sur 401 (session / token invalide)
    if (error.response?.status === 401) {
      // Google Calendar expiré, OAuth, probes : pas une session Rintintin morte
      if (urlLooksLikeNonSession401(error.config?.url)) {
        return Promise.reject(error);
      }
      // #995 — JWT expiré : reconnexion immédiate (le délai de grâce 2h masquait l’échec d’envoi facture).
      if (isRintintinJwtExpired401(error)) {
        const currentPath = `${window.location.pathname}${window.location.search ?? ''}${window.location.hash ?? ''}`;
        if (currentPath.startsWith('/app')) {
          localStorage.setItem('redirectAfterAuth', currentPath);
        }
        emitSessionInvalid();
        return Promise.reject(error);
      }
      if (!isProduction) {
        console.log('Interceptor Axios - Erreur 401 détectée:', {
          url: error.config?.url,
          status: error.response?.status,
          method: error.config?.method
        });
      }
      const token = localStorage.getItem('token');
      if (token) {
        const now = Date.now();

        // Vérifier si on est déjà sur une page d'auth
        const isOnAuthPage = window.location.pathname.includes('/login') ||
          window.location.pathname.includes('/auth/') ||
          window.location.pathname.includes('/signup');

        // Vérifier si on vient juste de se connecter (délai de grâce)
        const lastLoginTime = localStorage.getItem('lastLoginTime');
        const timeSinceLogin = lastLoginTime ? now - parseInt(lastLoginTime) : Infinity;

        // Incrémenter le compteur d'erreurs
        if (now - lastAuthErrorTime > 60000) { // Reset après 1 minute
          authErrorCount = 0;
        }
        authErrorCount++;
        lastAuthErrorTime = now;

        // Ne déconnecter que si :
        // 1. On n'est pas sur une page d'auth
        // 2. On n'est pas en train de se connecter
        // 3. On a eu plusieurs erreurs d'auth (3 ou plus)
        // 4. Ou si c'est une erreur 401/403 sur une route critique spécifique
        // 5. ET on n'est pas sur l'endpoint de création de disponibilités (qui peut avoir des erreurs 500)
        // 6. ET on n'est pas dans un contexte de vérification d'auth (AuthContext)
        const isCriticalRoute = (error.config?.url?.includes('/get-profile') ||
          error.config?.url?.includes('/get-pro')) &&
          !error.config?.url?.includes('/get-profiles'); // Éviter les faux positifs

        // Exclure l'endpoint de création de disponibilités de la déconnexion automatique
        const isAvailabilityCreation = error.config?.url?.includes('/create-service-availability');

        // Exclure les appels de vérification d'auth de l'AuthContext
        const isAuthContextCall = error.config?.url?.includes('/get-profile') ||
          error.config?.url?.includes('/get-pro') ||
          error.config?.url?.includes('/feature-flags');

        // Exclure l'endpoint de réponse à une reprogrammation (contre-proposition)
        const isRescheduleResponse = error.config?.url?.includes('/respond-reschedule');

        // Délai de grâce : plus long sur app native (JWT longue durée)
        const graceMs = Capacitor.isNativePlatform()
          ? 30 * 24 * 60 * 60 * 1000
          : 7200000;
        const isRecentlyLoggedIn = timeSinceLogin < graceMs;

        // Déconnexion seulement dans des cas très spécifiques
        if (!isOnAuthPage && !isRecentlyLoggedIn && !isAvailabilityCreation && !isAuthContextCall && !isRescheduleResponse &&
          (authErrorCount >= 5 || (isCriticalRoute && authErrorCount >= 2))) {

          if (!isProduction) {
            console.log('Déconnexion automatique après plusieurs erreurs:', {
              isOnAuthPage,
              isRecentlyLoggedIn,
              authErrorCount,
              isCriticalRoute,
              isAvailabilityCreation,
              isAuthContextCall,
              isRescheduleResponse,
              url: error.config?.url,
              timeSinceLogin
            });
          }

          const currentPath = `${window.location.pathname}${window.location.search ?? ''}${window.location.hash ?? ''}`;
          if (currentPath.startsWith('/app')) {
            localStorage.setItem('redirectAfterAuth', currentPath);
          }
          emitSessionInvalid();
        } else {
          // Log pour debug sans déconnexion
          if (!isProduction) {
            console.log('Erreur 401 détectée mais déconnexion évitée:', {
              isOnAuthPage,
              isRecentlyLoggedIn,
              authErrorCount,
              isCriticalRoute,
              isAvailabilityCreation,
              isAuthContextCall,
              isRescheduleResponse,
              url: error.config?.url,
              timeSinceLogin
            });
          }
        }
      }
    }

    return Promise.reject(error);
  }
);

const SessionNavigationBridge: React.FC = () => {
  const navigate = useNavigate();
  React.useEffect(() => {
    registerSessionNavigator(navigate);
    return () => unregisterSessionNavigator();
  }, [navigate]);
  return null;
};

/** Attend l'hydratation pro avant de rediriger hebergement / police-book (#894 QA). */
const DeferredBoardingNavigate: React.FC<{
  visibleTo: string;
  hiddenTo?: string;
}> = ({ visibleTo, hiddenTo = '/app/services' }) => {
  const { visible, ready } = useBoardingModuleGate();
  if (!ready) {
    return null;
  }
  return <Navigate to={visible ? visibleTo : hiddenTo} replace />;
};

const RedirectToWhatsNew: React.FC = () => {
  const { noteId } = useParams<{ noteId?: string }>();
  React.useEffect(() => {
    const target = noteId
      ? `https://whats-new-rintintin.netlify.app/${noteId}`
      : 'https://whats-new-rintintin.netlify.app';
    window.location.replace(target);
  }, [noteId]);
  return null;
};

// Composant conteneur qui gère l'affichage conditionnel du Footer
const AppContent: React.FC = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const laikaUiEnabled = resolveAssistantUiEnabled({
    featureFlagEnabled: isFeatureEnabled(FeatureFlag.LAIKA),
    forceLaikaEnv: process.env.REACT_APP_FORCE_LAIKA_ENABLED === 'true',
  });
  const mountLaikaChat = shouldMountLaikaChat({
    laikaUiEnabled,
    isAuthenticated,
    pathname: location.pathname,
  });
  const isMessagingPage = location.pathname === '/app/messaging' || location.pathname === '/app/messages';

  // Persister ?via= (Endorsely) et ?promo= (ambassadeur) pour le checkout
  React.useEffect(() => {
    resolveAmbassadorPromoFromLocation(location.search);
    const params = new URLSearchParams(location.search);
    const via = params.get('via');
    const endorselyParam = params.get('endorsely_referral');
    if (via && typeof via === 'string' && via.length <= 64) {
      const slug = via.trim();
      try {
        sessionStorage.setItem(AFFILIATE_VIA_STORAGE_KEY, slug);
      } catch {
        // ignore
      }
      // Endorsely : doc indique que le script lit le paramètre URL. On ajoute endorsely_referral si absent.
      if (!endorselyParam) {
        params.set('endorsely_referral', slug);
        const newSearch = params.toString();
        const newUrl = `${location.pathname}?${newSearch}${location.hash ? location.hash : ''}`;
        window.history.replaceState(window.history.state, '', newUrl);
      }
      // Remplir window.endorsely_referral pour que tout code (et le script Endorsely) voie la même valeur.
      // ⚠️ IMPORTANT : On ne met le slug (ex: akseleducation) QUE si le script Endorsely n'a pas encore mis un vrai UUID.
      try {
        const currentReferral = (window as { endorsely_referral?: string }).endorsely_referral;
        const isUuid = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(currentReferral || "");
        
        if (!currentReferral || !isUuid) {
          (window as { endorsely_referral?: string }).endorsely_referral = slug;
        }
      } catch {
        // ignore
      }
    }
  }, [location.search, AFFILIATE_VIA_STORAGE_KEY]);

  // Surveillance Endorsely : Le script est async, il peut mettre 2-3 secondes à transformer le slug en UUID.
  // On surveille window.endorsely_referral et on met à jour sessionStorage dès qu'on voit un UUID.
  React.useEffect(() => {
    const checkInterval = setInterval(() => {
      const current = (window as { endorsely_referral?: string }).endorsely_referral;
      if (current && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(current)) {
        // C'est un UUID ! On le sauvegarde
        try {
          const stored = sessionStorage.getItem('endorsely_uuid_captured');
          if (stored !== current) {
            sessionStorage.setItem('endorsely_uuid_captured', current);
            console.log('[Endorsely] UUID Captured and Stored:', current);
          }
        } catch (e) { /* ignore */ }
      }
    }, 1000); // Check every second

    return () => clearInterval(checkInterval);
  }, []);

  // Vérifier si on est sur mobile (écran < 768px)
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  // Vérifier si on est dans une application native (Capacitor)
  // Utiliser la méthode officielle de Capacitor pour une détection fiable
  const [isNativeApp, setIsNativeApp] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Détecter si on est dans une app native Capacitor avec la méthode officielle
    const checkNativeApp = () => {
      const isCapacitor = Capacitor.isNativePlatform();
      setIsNativeApp(isCapacitor);
    };

    checkNativeApp();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Déterminer si c'est une page marketing pour éviter les doublons de footer
  const MARKETING_PAGES = [
    '/',
    '/es',
    '/en',
    '/de',
    '/it',
    '/fonctionnalites',
    '/hebergement',
    '/cartes-cadeaux',
    '/educateur-canin',
    '/promeneur-canin',
    '/pet-sitter',
    '/pension-canine',
    '/comportementaliste',
    '/toiletteur',
    '/es/educador-canino',
    '/es/paseador-perros',
    '/es/cuidador-perros',
    '/es/residencia-canina',
    '/es/etologo-canino',
    '/es/peluqueria-canina',
    '/en/dog-trainer',
    '/en/dog-walker',
    '/en/pet-sitter',
    '/en/dog-boarding',
    '/en/dog-behaviorist',
    '/en/dog-groomer',
    '/de',
    '/de/hundetrainer',
    '/de/hundeausfuehrer',
    '/de/tiersitter',
    '/de/hundepension',
    '/de/verhaltensberater',
    '/de/hundefriseur',
    '/it',
    '/it/addestratore-cani',
    '/it/passeggiatore-cani',
    '/it/pet-sitter',
    '/it/pensione-cani',
    '/it/etologo-cani',
    '/it/toelettatore-cani',
    '/blog',
    '/partenaires',
    '/partenaires/',
    '/v2',
    '/offre-configuration',
    '/facturation-electronique',
    '/acaced',
    '/comparatif-logiciel-educateur-canin',
    '/comparatif-logiciel-pet-sitter',
    '/comparatif-logiciel-pension-canine',
    '/migrer-whatsapp-excel-vers-logiciel-pro-chien',
    '/modele-facture-prestation-chien',
    '/modele-contrat-garde-chien',
    '/tarifs-educateur-canin-logiciel',
    '/logiciel-dog-sitter',
    '/loisir-canin',
    '/ecole-club-canin-logiciel',
    '/alternatives-logiciel-pro-chien',
    // Structure FR : un seul footer (mk-footer du PublicLayout)
    '/terms-of-use',
    '/privacy-policy',
    '/vs-canisuivi',
    '/vs-hunimalis',
    '/vs-kookie',
    '/faq',
    '/avis-rintintin-pro',
  ];

  const marketingPath = normalizeMarketingPathname(location.pathname);
  const isMarketingPage = MARKETING_PAGES.includes(marketingPath) ||
    marketingPath.startsWith('/fonctionnalites/') ||
    marketingPath.startsWith('/blog/') ||
    isBlogPath(marketingPath) ||
    isMarketPath(marketingPath);

  // Redirection automatique vers login sur app native si on est sur la landing page
  React.useEffect(() => {
    if (isNativeApp && location.pathname === '/') {
      redirectToLogin();
    }
  }, [isNativeApp, location.pathname]);

  // Masquer le footer sur l'application native, sur les pages marketing (qui ont leur propre footer)
  // ou sur Messaging en mode mobile
  const shouldHideFooter = isNativeApp || isMarketingPage || (isMessagingPage && isMobile);

  return (
    <>
      <SessionNavigationBridge />
      <MobileAuthHandler />
      {/* Toast notifications globales */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ zIndex: BILLING_TOAST_Z }}
      />

      <RouteSuspense>
        <Routes>
        {/* Page d'accueil - Desktop et responsive uniquement */}
        <Route path="/" element={<PublicLayout />}>
          <Route
            index
            element={
              !isNativeApp ? (
                <LandingPageV2 />
              ) : (
                <MaintenanceGate>
                  <LoginPage />
                </MaintenanceGate>
              )
            }
          />
          <Route path="v2" element={<LandingPageV2 />} />
        </Route>

        {/* ==================== PAGES PUBLIQUES ==================== */}
        <Route element={<PublicLayout />}>
          <Route
            path="/login"
            element={
              <MaintenanceGate>
                <LoginPage />
              </MaintenanceGate>
            }
          />
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route path="/admin-handoff" element={<AdminHandoffPage />} />
          <Route
            path="/password-forgotten"
            element={
              <MaintenanceGate>
                <PasswordForgottenPage />
              </MaintenanceGate>
            }
          />
          <Route
            path="/reset-password"
            element={
              <MaintenanceGate>
                <ResetPasswordPage />
              </MaintenanceGate>
            }
          />
          {/* Sur mobile natif (iOS/Android), bloquer les pages d'inscription */}
          {/* Apple Guideline 3.1.1 : pas de création de compte business dans l'app */}
          <Route
            path="/signup-user"
            element={
              isNativeApp ? (
                <MaintenanceGate>
                  <LoginPage />
                </MaintenanceGate>
              ) : (
                <MaintenanceGate>
                  <SignupUserPage />
                </MaintenanceGate>
              )
            }
          />
          <Route path="/auth/google/callback" element={
            <MaintenanceGate>
              <GoogleAuthCallback />
            </MaintenanceGate>
          } />
          <Route path="/auth/google/account-linking-callback" element={
            <MaintenanceGate>
              <GoogleAccountLinkingCallbackPage />
            </MaintenanceGate>
          } />
          <Route
            path="/signup-professional"
            element={
              isNativeApp ? (
                <MaintenanceGate>
                  <LoginPage />
                </MaintenanceGate>
              ) : (
                <MaintenanceGate>
                  <SignUpProPage />
                </MaintenanceGate>
              )
            }
          />
          <Route path="/signup-pro" element={<Navigate to="/signup-professional" replace />} />
          <Route path="/signup" element={<Navigate to="/signup-user" replace />} />
          <Route path="/no-pro-profile" element={<NoProProfile />} />

          {/* Pages Hub SEO par verticale */}
          <Route path="/educateur-canin" element={<EducateurCaninPage />} />
          <Route path="/promeneur-canin" element={<PromeneurCaninPage />} />
          <Route path="/pet-sitter" element={<PetSitterPage />} />
          <Route path="/logiciel-dog-sitter" element={<LogicielDogSitterPage />} />
          <Route path="/ecole-club-canin-logiciel" element={<EcoleClubCaninPage />} />
          <Route path="/pension-canine" element={<PensionPage />} />
          <Route path="/comportementaliste" element={<ComportementalistePage />} />
          <Route path="/toiletteur" element={<TailleursPage />} />
          <Route path="/loisir-canin" element={<LoisirCaninPage />} />

          {/* Hubs marché (pays ≠ langue), pas de hreflang régional ; Flandre = /be/nl ; NL = /nl */}
          <Route path="/be" element={<BelgiumHubPage />} />
          <Route path="/be/nl" element={<BelgiumHubPage />} />
          <Route path="/be/educateur-canin" element={<BelgiumEducateurCaninPage />} />
          <Route path="/be/pet-sitter" element={<BelgiumPetSitterPage />} />
          <Route path="/be/nl/educateur-canin" element={<BelgiumEducateurCaninPage />} />
          <Route path="/be/nl/pet-sitter" element={<BelgiumPetSitterPage />} />
          <Route path="/nl" element={<NetherlandsHubPage />} />
          <Route path="/nl/educateur-canin" element={<NetherlandsEducateurCaninPage />} />
          <Route path="/nl/pet-sitter" element={<NetherlandsPetSitterPage />} />

          {/* Routes marketing ES (slugs localisés). REACT_APP_LOCALE_ROUTES_ES=true en prod */}
          {isLocaleRoutesEsEnabled() && (
            <>
              <Route path="/es" element={<LandingPageV2 />} />
              <Route path="/es/" element={<LandingPageV2 />} />
              <Route path="/es/educador-canino" element={<EducateurCaninPage />} />
              <Route path="/es/paseador-perros" element={<PromeneurCaninPage />} />
              <Route path="/es/cuidador-perros" element={<PetSitterPage />} />
              <Route path="/es/residencia-canina" element={<PensionPage />} />
              <Route path="/es/etologo-canino" element={<ComportementalistePage />} />
              <Route path="/es/peluqueria-canina" element={<TailleursPage />} />
            </>
          )}

          {isLocaleRoutesEnEnabled() && (
            <>
              <Route path="/en" element={<LandingPageV2 />} />
              <Route path="/en/" element={<LandingPageV2 />} />
              <Route path="/en/dog-trainer" element={<EducateurCaninPage />} />
              <Route path="/en/dog-trainer/" element={<EducateurCaninPage />} />
              <Route path="/en/dog-walker" element={<PromeneurCaninPage />} />
              <Route path="/en/dog-walker/" element={<PromeneurCaninPage />} />
              <Route path="/en/pet-sitter" element={<PetSitterPage />} />
              <Route path="/en/pet-sitter/" element={<PetSitterPage />} />
              <Route path="/en/dog-boarding" element={<PensionPage />} />
              <Route path="/en/dog-boarding/" element={<PensionPage />} />
              <Route path="/en/dog-behaviorist" element={<ComportementalistePage />} />
              <Route path="/en/dog-behaviorist/" element={<ComportementalistePage />} />
              <Route path="/en/dog-groomer" element={<TailleursPage />} />
              <Route path="/en/dog-groomer/" element={<TailleursPage />} />
            </>
          )}

          {isLocaleRoutesItEnabled() && (
            <>
              <Route path="/it" element={<LandingPageV2 />} />
              <Route path="/it/" element={<LandingPageV2 />} />
              <Route path="/it/addestratore-cani" element={<EducateurCaninPage />} />
              <Route path="/it/passeggiatore-cani" element={<PromeneurCaninPage />} />
              <Route path="/it/pet-sitter" element={<PetSitterPage />} />
              <Route path="/it/pensione-cani" element={<PensionPage />} />
              <Route path="/it/etologo-cani" element={<ComportementalistePage />} />
              <Route path="/it/toelettatore-cani" element={<TailleursPage />} />
            </>
          )}

          {isLocaleRoutesDeEnabled() && (
            <>
              <Route path="/de" element={<LandingPageV2 />} />
              <Route path="/de/" element={<LandingPageV2 />} />
              <Route path="/de/hundetrainer" element={<EducateurCaninPage />} />
              <Route path="/de/hundeausfuehrer" element={<PromeneurCaninPage />} />
              <Route path="/de/tiersitter" element={<PetSitterPage />} />
              <Route path="/de/hundepension" element={<PensionPage />} />
              <Route path="/de/verhaltensberater" element={<ComportementalistePage />} />
              <Route path="/de/hundefriseur" element={<TailleursPage />} />
            </>
          )}

          <Route path="/partenaires" element={<PartenairesPublicPage />} />
          <Route path="/partenaires/lupi" element={<LupiPage />} />
          <Route path="/partenaires/osmoz" element={<OsmozPage />} />
          <Route path="/partenaires/mediavet" element={<MediavetPage />} />
          <Route path="/partenaires/tresors-d-ulysse" element={<TresorsDUlyssePage />} />
          <Route path="/partenaires/mfec" element={<MfecPage />} />
          <Route path="/partenaires/:slug" element={<PartenaireDetailPage />} />

          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/suppression-donnees" element={<DataDeletion />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/pricing" element={<SubscriptionPage />} />
          <Route path="/offre-configuration" element={<OffreConfigurationPage />} />
          <Route path="/facturation-electronique" element={<EFacturationOfferPage />} />
          <Route path="/acaced" element={<AcacedOfferPage />} />
          <Route path="/comparatif-logiciel-educateur-canin" element={<SoftwareComparisonPage />} />
          <Route path="/comparatif-logiciel-pet-sitter" element={<SoftwareComparisonPetSitterPage />} />
          <Route path="/comparatif-logiciel-pension-canine" element={<SoftwareComparisonPensionPage />} />
          <Route
            path="/migrer-whatsapp-excel-vers-logiciel-pro-chien"
            element={<MigrationPlaybookPage />}
          />
          <Route path="/modele-facture-prestation-chien" element={<ToolResourcePage />} />
          <Route path="/modele-contrat-garde-chien" element={<ToolResourcePage />} />
          <Route path="/tarifs-educateur-canin-logiciel" element={<ToolResourcePage />} />
          <Route path="/alternatives" element={<Navigate to="/alternatives-logiciel-pro-chien/" replace />} />
          <Route path="/alternatives-logiciel-pro-chien" element={<AlternativesHubPage />} />
          <Route path="/vs-canisuivi" element={<VsCanisuiviPage />} />
          <Route path="/vs-hunimalis" element={<VsHunimalisPage />} />
          <Route path="/vs-kookie" element={<VsKookiePage />} />
          <Route path="/faq" element={<FaqHubPage />} />
          <Route path="/avis-rintintin-pro" element={<AvisRintintinProPage />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/subscription-success" element={<SubSuccessPage />} />

          <Route path="/quoi-de-neuf" element={<RedirectToWhatsNew />} />
          <Route path="/quoi-de-neuf/:noteId" element={<RedirectToWhatsNew />} />
          <Route path="/app/quoi-de-neuf" element={<RedirectToWhatsNew />} />
          <Route path="/app/quoi-de-neuf/:noteId" element={<RedirectToWhatsNew />} />

          {/* Blog public (SEO) */}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          <Route path="/blog/category/:categorySlug" element={<BlogPage />} />
          {localizedBlogRoutes()}

          {/* Pages Fonctionnalités (SEO) */}
          <Route path="/fonctionnalites" element={<FeaturesPage />} />
          <Route path="/fonctionnalites/planning-reservations" element={<PlanningReservationsPage />} />
          <Route path="/fonctionnalites/messagerie" element={<MessagingFeaturePage />} />
          <Route path="/fonctionnalites/paiement-facturation" element={<PaiementFacturationPage />} />
          <Route path="/fonctionnalites/forfaits-credits" element={<ForfaitsCreditsPage />} />
          <Route path="/fonctionnalites/cartes-cadeaux" element={<CartesCadeauxPage />} />
          <Route path="/fonctionnalites/emails" element={<EmailsProPage />} />
          <Route path="/fonctionnalites/groupes-clients" element={<GroupesClientsPage />} />
          <Route path="/fonctionnalites/double-sync-google" element={<DoubleSyncGooglePage />} />
          <Route path="/fonctionnalites/frais-kilometriques" element={<FraisKilometriquesPage />} />
          <Route path="/fonctionnalites/rappels-sms" element={<RappelsSmsPage />} />
          <Route path="/fonctionnalites/gestion-clients-animaux" element={<GestionClientsAnimauxPage />} />
          <Route path="/fonctionnalites/statistiques" element={<StatistiquesPage />} />
          <Route path="/fonctionnalites/profil-professionnel" element={<ProfilProfessionnelPage />} />
          <Route path="/fonctionnalites/pension-hebergement" element={<PensionHebergementPage />} />
          <Route path="/fonctionnalites/application-mobile" element={<ApplicationMobilePage />} />

          {/* Language Test Page */}
          <Route path="/language-test" element={<LanguageTestPage />} />

          {/* Laboratoire iframe widgets (dev ou REACT_APP_ENABLE_WIDGET_LAB) */}
          <Route path="/dev/widget-lab" element={<WidgetLabPage />} />

          <Route
            path="/hebergement"
            element={<Navigate to="/fonctionnalites/pension-hebergement" replace />}
          />
          <Route
            path="/cartes-cadeaux"
            element={<Navigate to="/fonctionnalites/cartes-cadeaux" replace />}
          />

          {/* 404 géré par le Layout Public (=> Pas de sidebar) */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* ==================== PAGES PRIVÉES (/app) ==================== */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <PrivateLayout />
            </ProtectedRoute>
          }
        >
          <Route path="" element={<AccueilPage />} />
          <Route path="demarrage" element={<StartupPage />} />
          <Route path="acaced" element={<AcacedPage />} />
          <Route path="offre-essai" element={<WelcomeTrialOfferPage />} />
          <Route path="offre-essai/prix" element={<WelcomePricingTablePage />} />
          <Route path="profil" element={<Navigate to="/app/profil-v2" replace />} />
          <Route path="profil-v2" element={<ProfilePageV2 />} />
          <Route path="planning" element={<PlanningPage />} />
          <Route
            path="evenements"
            element={<Navigate to="/app/services?filter=ponctuel" replace />}
          />
          <Route path="planning/unavailabilities" element={<UnavailabilitiesPage />} />
          <Route path="reservations" element={<ReservationsPage />} />
          <Route path="forfaits" element={<ForfaitsPage />} />
          <Route path="forfaits/credits" element={<Navigate to="/app/credits" replace />} />
          <Route path="credits" element={<ForfaitsPage />} />
          <Route path="cartes-cadeaux" element={<ForfaitsPage />} />
          <Route path="abonnements" element={<ForfaitsPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="clients" element={<ClientsChiensPage />} />
          <Route path="suivis" element={<FollowHubPage />} />
          <Route path="clients/segments" element={<ClientSegmentsPage />} />
          <Route path="chiens" element={<ClientsChiensPage />} />
          <Route path="clients-chiens" element={<ClientsChiensPage />} />
          <Route path="equipe" element={<TeamPage />} />
          <Route path="animal/:animalId" element={<AnimalDetailPage />} />
          <Route path="animal/:animalId/follow-up" element={<AnimalFollowUpPage />} />
          <Route path="chiens/:animalId" element={<AnimalDetailPage />} />
          <Route path="proprio/:clientId" element={<ProprioDetailPage />} />
          <Route path="pro/:clientId" element={<ProprioDetailPage />} />
          <Route path="clients/:clientId" element={<ProprioDetailPage />} />
          <Route path="service/:serviceId/availability" element={<AvailabilityPage />} />
          <Route path="booking/:bookingId" element={<BookingDetailPage />} />
          <Route path="session/:sessionId" element={<SessionDetailPage />} />
          <Route path="notes" element={<AllNotesPage />} />
          <Route path="subscription/hebergement" element={<BoardingModuleOfferPage />} />
          <Route path="subscription" element={<SubscriptionPage />} />
          <Route path="ambassadeur" element={<AmbassadeurPage />} />
          <Route path="sub-success" element={<SubSuccessPage />} />
          <Route path="boarding-sub-success" element={<BoardingSubSuccessPage />} />
          <Route path="sub-cancel" element={<SubCancelPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="partenaires" element={<PartenairesPage />} />
          <Route path="partenaires/lupi" element={<LupiPage />} />
          <Route path="partenaires/osmoz" element={<OsmozInAppPage />} />
          <Route path="partenaires/mediavet" element={<MediavetPage />} />
          <Route path="partenaires/tresors-d-ulysse" element={<TresorsDUlyssePage />} />
          <Route path="partenaires/mfec" element={<MfecPage />} />
          <Route path="design-system" element={<DesignSystemPage />} />
          <Route
            path="billing"
            element={
              <RequireCountryFeature feature="billingUi">
                <RequireBillingModule>
                  <BillingPage />
                </RequireBillingModule>
              </RequireCountryFeature>
            }
          />
          <Route
            path="billing/invoice"
            element={
              <RequireCountryFeature feature="billingUi">
                <RequireBillingModule>
                  <InvoiceDetailPage />
                </RequireBillingModule>
              </RequireCountryFeature>
            }
          />
          <Route
            path="billing/invoice/:invoiceId"
            element={
              <RequireCountryFeature feature="billingUi">
                <RequireBillingModule>
                  <InvoiceDetailPage />
                </RequireBillingModule>
              </RequireCountryFeature>
            }
          />
          <Route
            path="quotes"
            element={
              <RequireCountryFeature feature="billingUi">
                <RequireBillingModule>
                  <QuotesPage />
                </RequireBillingModule>
              </RequireCountryFeature>
            }
          />
          <Route
            path="devis"
            element={
              <RequireCountryFeature feature="billingUi">
                <RequireBillingModule>
                  <QuotesPage />
                </RequireBillingModule>
              </RequireCountryFeature>
            }
          />
          <Route path="comptabilite" element={<AccountingPage />} />
          <Route path="finance" element={<FinancePage />} />
          <Route path="marketing" element={<MarketingToolsPage />} />
          <Route path="stripe-setup-complete" element={<StripeSetupComplete />} />
          {/* TEMPORAIRE : V2 en remplacement de V1 pour test */}
          <Route path="stats" element={<ProStatsPageV2 />} />
          <Route path="stats/v1" element={<Navigate to="/app/stats" replace />} />
          <Route path="stats/v2" element={<ProStatsPageV2 />} />
          <Route
            path="pension"
            element={
              <RequireCountryFeature feature="boarding">
                <BoardingPage />
              </RequireCountryFeature>
            }
          />
          <Route
            path="hebergement"
            element={<DeferredBoardingNavigate visibleTo="/app/pension" />}
          />
          <Route path="client/:clientId" element={<RedirectLegacyAppClientPath />} />
          <Route
            path="police-book"
            element={
              <DeferredBoardingNavigate visibleTo="/app/pension?tab=police-book" />
            }
          />
          {/* <Route path="police-book" element={<PoliceBookPage />} /> */}
          <Route
            path="stay/:stayId"
            element={
              <RequireCountryFeature feature="boarding">
                <StayDetailPage />
              </RequireCountryFeature>
            }
          />
          <Route path="messaging" element={<BackOfficeMessagingPage />} />
          {/* Deep link push / notifications : /app/messages/{twilio_conversation_sid} */}
          <Route path="messages/:conversationSid" element={<BackOfficeMessagingPage />} />
          <Route path="messages" element={<BackOfficeMessagingPage />} />
          <Route path="no-premium-messaging" element={<NoPremiumMessaging />} />
          <Route path="association/:associationSlug/planning" element={<AssociationPlanningPage />} />
          <Route path="association/:associationSlug/members" element={<AssociationMembersPage />} />
          <Route path="association/:associationSlug/member/:memberId" element={<AssociationMemberProfilePage />} />
          <Route path="association/:associationSlug/clients" element={<AssociationClientsPage />} />
          <Route path="association/:associationSlug/events" element={<AssociationEventsPage />} />
          <Route path="association/:associationSlug/messaging" element={<AssociationMessagingPage />} />
          <Route path="association/:associationSlug/cgv" element={<AssociationCGVPage />} />
          <Route path="association/:associationSlug/settings" element={<AssociationSettingsPage />} />
          <Route path="associations" element={<AssociationDashboard />} />
          <Route path="sms" element={<RedirectSmsToCommunication />} />
          <Route
            path="laika"
            element={
              laikaUiEnabled ? <LaikaPage /> : <Navigate to="/app" replace />
            }
          />

          {/* Notification Management */}
          <Route path="notifications" element={<NotificationHistoryPage />} />

          <Route path="*" element={<PrivateNotFoundPage />} />
        </Route>
        </Routes>
      </RouteSuspense>

      {/* ==================== FOOTER GLOBAL ==================== */}
      {!shouldHideFooter && <Footer />}

      {/* ==================== CHAT WIDGET (auth + /app uniquement) ==================== */}
      {mountLaikaChat && <ChatWidget />}
    </>
  );
};

const AppThemeRoot: React.FC = () => {
  const { i18n } = useTranslation();
  const dateLocale = (i18n.language || 'fr').split('-')[0] || 'fr';
  const { isDark, paletteId } = useDevNightThemeSnapshot();
  const muiTheme = useMemo(
    () => createAppTheme(isDark ? 'dark' : 'light', paletteId),
    [isDark, paletteId]
  );

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dateLocale}>
        <ChatProvider>
          <LaikaChatFlagSync />
          <Router>
            <MetaPixelPageViews />
            <PostHogPageViews />
            <PostHogIdentitySync />
            <AppContent />
            <CookieConsentBanner />
          </Router>
        </ChatProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  // Configuration de la barre de statut iOS
  React.useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      StatusBar.setStyle({ style: Style.Light });
      StatusBar.setBackgroundColor({ color: '#A5765C' });
      StatusBar.setOverlaysWebView({ overlay: false });
    }
  }, []);

  return (
    <FeatureFlagsProvider>
      <GoogleMapsProvider>
        <AppThemeRoot />
      </GoogleMapsProvider>
    </FeatureFlagsProvider>
  );
};

export default App;
